import { FirebaseError } from "firebase/app";
import {logger, LogCode, LogLevel} from "@/logger";
import {
  FirebasePermissionDeniedError,
  MissingSessionError,
  InvalidLinkError,
  ExpiredLinkError,
  InvalidOrExpiredOrUsedLinkError,
  PermissionDeniedError,
  SignInFailureError,
  TooManyRequestsError,
  NetworkError,
} from "@/errors.ts";
import { AuthAction, ErrorMap, ErrorMapItem } from "@/types.ts";
import { AuthErrorCode } from "@/enums.ts";

const DEFAULT_ERROR_MAP_ITEM: ErrorMapItem = {
  errorConstructor: SignInFailureError,
  logCode: LogCode.FAILED_TO_SIGN_IN_WITH_EMAIL_LINK, // Will be overridden based on action
  logMessage: 'Unhandled error',
  logLevel: LogLevel.ERROR,
};


const defaultErrorMap: ErrorMap = {
  // Firebase errors:
  [AuthErrorCode.TOO_MANY_REQUESTS]: {
    errorConstructor: TooManyRequestsError,
    logCode: LogCode.FAILED_TO_SEND_EMAIL_TOO_MANY_REQUESTS, // Will be overridden per action
    logMessage: 'Too many requests',
    logLevel: LogLevel.WARN,
  },
  [AuthErrorCode.NETWORK_REQUEST_FAILED]: {
    errorConstructor: NetworkError,
    logCode: LogCode.FAILED_TO_SEND_EMAIL_NETWORK_ERROR, // Will be overridden per action
    logMessage: 'Network error',
    logLevel: LogLevel.WARN,
  },
  // Permission Denied is returned as this Firebase error code but is handled
  // differently (see AuthErrorCode.CUSTOM_PERMISSION_DENIED)
  // Otherwise thrown if server encounters error
  [AuthErrorCode.INTERNAL_ERROR]: {
    errorConstructor: SignInFailureError,
    logCode: LogCode.FAILED_TO_SEND_EMAIL_INTERNAL_SERVER_ERROR, // Will be overridden per action
    logMessage: 'Internal server error',
    logLevel: LogLevel.ERROR,
  },
}

const signInWithEmailLinkErrorMap: ErrorMap = {
  // =======================================
  // Custom error codes thrown by frontend:
  //
  // Thrown if LocalStorage is missing value for emailForSignIn
  // (typically thrown if user tries to sign in on different device than they requested the link on)
  [AuthErrorCode.CUSTOM_MISSING_SESSION]: {
    errorConstructor: MissingSessionError,
    logCode: LogCode.FAILED_TO_SIGN_IN_WITH_EMAIL_LINK_MISSING_SESSION,
    logMessage: 'Missing session',
    logLevel: LogLevel.WARN,
  },
  // Thrown before we attempt to sign in, if link is not valid
  [AuthErrorCode.CUSTOM_INVALID_SIGNIN_LINK]: {
    errorConstructor: InvalidLinkError,
    logCode: LogCode.FAILED_TO_SIGN_IN_WITH_EMAIL_LINK_INVALID_LINK,
    logMessage: 'Invalid link',
    logLevel: LogLevel.WARN,
  },
  // Thrown from Firebase as AuthErrorCode.INTERNAL_ERROR but handled differently here
  // Thrown if user.isActive = false.
  [AuthErrorCode.CUSTOM_PERMISSION_DENIED]: {
    errorConstructor: PermissionDeniedError,
    logCode: LogCode.FAILED_TO_SIGN_IN_WITH_EMAIL_LINK_PERMISSION_DENIED,
    logMessage: 'Permission denied',
    logLevel: LogLevel.WARN,
  },
  // =======================================
  // Firebase errors:
  //
  [AuthErrorCode.USER_DISABLED]: {
    errorConstructor: PermissionDeniedError,
    logCode: LogCode.FAILED_TO_SIGN_IN_WITH_EMAIL_LINK_PERMISSION_DENIED,
    logMessage: 'User disabled',
    logLevel: LogLevel.WARN,
  },
  // Link is expired, invalid or has already been used
  [AuthErrorCode.INVALID_ACTION_CODE]: {
    errorConstructor: InvalidOrExpiredOrUsedLinkError,
    logCode: LogCode.FAILED_TO_SIGN_IN_WITH_EMAIL_LINK_INVALID_OR_EXPIRED_OR_USED_LINK,
    logMessage: 'Invalid, expired or used link',
    logLevel: LogLevel.WARN,
  },
  // Link expired after 6 hours
  [AuthErrorCode.EXPIRED_ACTION_CODE]: {
    errorConstructor: ExpiredLinkError,
    logCode: LogCode.FAILED_TO_SIGN_IN_WITH_EMAIL_LINK_EXPIRED_LINK,
    logMessage: 'Expired link',
    logLevel: LogLevel.WARN,
  },
}

const createErrorMapForAction = (baseErrorMap: ErrorMap, actionLogCodes: Record<string, LogCode>): ErrorMap => {
  const result: ErrorMap = {};

  for (const [errorCode, errorMapping] of Object.entries(baseErrorMap)) {
    const actionSpecificLogCode = actionLogCodes[errorCode];
    result[errorCode] = {
      ...errorMapping,
      logCode: actionSpecificLogCode || errorMapping.logCode
    };
  }

  return result;
}

const AUTH_ACTIONS: Record<string, AuthAction> = {
  SEND_EMAIL_LINK: {
    baseLogCode: LogCode.FAILED_TO_SEND_EMAIL,
    logMessage: 'Failed to send email link',
    errorMap: createErrorMapForAction(defaultErrorMap, {
      [AuthErrorCode.TOO_MANY_REQUESTS]: LogCode.FAILED_TO_SEND_EMAIL_TOO_MANY_REQUESTS,
      [AuthErrorCode.NETWORK_REQUEST_FAILED]: LogCode.FAILED_TO_SEND_EMAIL_NETWORK_ERROR,
      [AuthErrorCode.INTERNAL_ERROR]: LogCode.FAILED_TO_SEND_EMAIL_INTERNAL_SERVER_ERROR,
    })
  },
  SIGN_IN_WITH_EMAIL_LINK: {
    baseLogCode: LogCode.FAILED_TO_SIGN_IN_WITH_EMAIL_LINK,
    logMessage: 'Failed to sign in with email link',
    // Sign in has more errors to handle
    errorMap: createErrorMapForAction({
      ...defaultErrorMap,
      ...signInWithEmailLinkErrorMap,
    }, {
      [AuthErrorCode.TOO_MANY_REQUESTS]: LogCode.FAILED_TO_SIGN_IN_WITH_EMAIL_LINK_TOO_MANY_REQUESTS,
      [AuthErrorCode.NETWORK_REQUEST_FAILED]: LogCode.FAILED_TO_SIGN_IN_WITH_EMAIL_LINK_NETWORK_ERROR,
      [AuthErrorCode.INTERNAL_ERROR]: LogCode.FAILED_TO_SIGN_IN_WITH_EMAIL_LINK_INTERNAL_SERVER_ERROR,
    })
  },
  SIGN_OUT: {
    baseLogCode: LogCode.FAILED_TO_SIGN_OUT,
    logMessage: 'Failed to sign out',
    errorMap: createErrorMapForAction(defaultErrorMap, {
      [AuthErrorCode.TOO_MANY_REQUESTS]: LogCode.FAILED_TO_SIGN_OUT_TOO_MANY_REQUESTS,
      [AuthErrorCode.NETWORK_REQUEST_FAILED]: LogCode.FAILED_TO_SIGN_OUT_NETWORK_ERROR,
      [AuthErrorCode.INTERNAL_ERROR]: LogCode.FAILED_TO_SIGN_OUT_INTERNAL_SERVER_ERROR,
    })
  }
}

// Obtains error code for above errorMap
const getErrorCode = (error: unknown, errorMap: ErrorMap): string => {
  if (isFirebasePermissionDeniedError(error)) {
    return AuthErrorCode.CUSTOM_PERMISSION_DENIED;
  } else if (error instanceof FirebaseError) {
    // Check if the specific Firebase error code exists in our errorMap
    if (errorMap[error.code]) {
      return error.code;
    }
  }
  return AuthErrorCode.DEFAULT;
}

// Appends original error context to readable error message displayed to user
const appendReferenceId = (message: string, correlationId: string) =>
  `${message}\n\nReference ID: ${correlationId}`;

const createErrorToThrow = (
  errorMapping: ErrorMapItem,
  originalError: unknown,
  correlationId: string
) => {
  const baseError = new errorMapping.errorConstructor();

  if (errorMapping.errorConstructor === PermissionDeniedError) {
    return new errorMapping.errorConstructor(
      appendReferenceId(baseError.message, correlationId)
    );
  }

  const baseMessage = baseError.message;
  const originalMessage = originalError instanceof Error ? originalError.message : '';

  const finalMessage = originalMessage
    ? `${baseMessage}\n\n${originalMessage}`
    : baseMessage;

  return new errorMapping.errorConstructor(
    appendReferenceId(finalMessage, correlationId)
  );
};

// Associates passed code with above errorMap, logs the error and throws it to the UI to be displayed
const handleError = (originalError: unknown, authAction: AuthAction, correlationId: string) => {
  const errorCode = getErrorCode(originalError, authAction.errorMap);
  const errorMapping = authAction.errorMap[errorCode] ?? {
    ...DEFAULT_ERROR_MAP_ITEM,
    logCode: authAction.baseLogCode
  };
  const errorToThrow = createErrorToThrow(errorMapping, originalError, correlationId);

  logger[errorMapping.logLevel](
    `${authAction.logMessage}: ${errorMapping.logMessage}: ${errorCode}`,
    {
      component: 'stores/auth.ts',
      pathname: window.location.pathname,
      route: window.location.pathname + window.location.search,
      logCode: errorMapping.logCode,
      correlationId,
    },
    originalError ?? errorToThrow
  );
  throw errorToThrow;
}

const isFirebasePermissionDeniedError = (
  error: unknown
): error is FirebasePermissionDeniedError =>
  error instanceof FirebaseError &&
  error?.message?.includes("PERMISSION_DENIED");

export {
  handleError,
  AUTH_ACTIONS,
  // Exported for tests
  getErrorCode,
  createErrorMapForAction,
  createErrorToThrow,
  isFirebasePermissionDeniedError,
}
